// Issue Templates — two-level admin CRUD.
//
// Level 1: list of templates (containers) — name, description,
//   item count, sites assigned.
// Level 2: click a template → detail view showing items CRUD
//   (label, description, priority preset, active toggle).
//
// A template is assigned to a site from the Site detail screen
// (Settings → Sites → pick a site). Locations under that site
// inherit the assignment automatically.

import { useState } from 'react'
import {
  Box, Breadcrumbs, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Link, Stack, Switch, TextField,
  Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { IssueTemplate, IssueTemplateItem } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  low:      { label: 'Low',      color: '#22C55E' },
  medium:   { label: 'Medium',   color: '#F59E0B' },
  high:     { label: 'High',     color: '#F97316' },
  critical: { label: 'Critical', color: '#EF4444' },
}

export function IssueTemplatesTab() {
  // Local state: which template's detail is open (null = list view).
  const [openedTemplate, setOpenedTemplate] = useState<IssueTemplate | null>(null)

  if (openedTemplate) {
    return (
      <TemplateDetail
        template={openedTemplate}
        onBack={() => setOpenedTemplate(null)}
        onTemplateChanged={(t) => setOpenedTemplate(t)}
      />
    )
  }

  return <TemplateList onOpen={setOpenedTemplate} />
}

// ─── Level 1: list of templates ──────────────────────────────────

function TemplateList({ onOpen }: { onOpen: (t: IssueTemplate) => void }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<IssueTemplate | null>(null)
  const confirmDlg = useConfirm()

  const templatesQ = useQuery({
    queryKey: ['issue-templates'],
    queryFn: () => api.issueTemplates.list(),
  })
  const templates: IssueTemplate[] = Array.isArray(templatesQ.data) ? templatesQ.data : []

  const deleteM = useMutation({
    mutationFn: (id: string) => api.issueTemplates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issue-templates'] })
      toast.success('Template deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const handleDelete = async (t: IssueTemplate) => {
    const inUse = t.assigned_site_count > 0
    const ok = await confirmDlg({
      title: 'Delete this template?',
      message: inUse
        ? `"${t.name}" is currently assigned to ${t.assigned_site_count} site${t.assigned_site_count === 1 ? '' : 's'}. Deleting it unlinks those sites (their QR forms will fall back to a free-text input). Every item in the template will also be removed.`
        : `"${t.name}" and all ${t.item_count} of its items will be permanently removed.`,
      confirmLabel: 'Delete template',
      variant: 'danger',
    })
    if (ok) deleteM.mutate(t.id)
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
        justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Issue templates</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Named lists of issue titles reporters pick from when they scan a
            location QR. Assign a template to a site from{' '}
            <strong>Settings → Sites → &lt;site&gt;</strong>. Locations under
            that site inherit its template automatically. Sites without a
            template fall back to a free-text input.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}>
          New template
        </Button>
      </Stack>

      {templatesQ.isLoading ? (
        <Typography sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>Loading…</Typography>
      ) : templates.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 6, border: '1px dashed', borderColor: 'divider',
          borderRadius: 2, color: 'text.disabled',
        }}>
          <QuestionAnswerOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No templates yet</Typography>
          <Typography variant="body2" sx={{ maxWidth: 420, mx: 'auto' }}>
            Create a template (e.g. "Warehouse issues"), add items to it, then
            assign it to one or more sites.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {templates.map((t) => (
            <Box key={t.id} sx={{
              p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
              opacity: t.is_active ? 1 : 0.55,
              transition: 'background-color 120ms ease',
              '&:hover': { bgcolor: 'action.hover' },
            }}
              onClick={() => onOpen(t)}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {t.name}
                  </Typography>
                  <Chip label={`${t.active_item_count} / ${t.item_count} items`} size="small" sx={{
                    height: 20, fontSize: 10.5, fontWeight: 700,
                    bgcolor: '#3B82F622', color: '#3B82F6',
                  }} />
                  {t.assigned_site_count > 0 && (
                    <Chip label={`${t.assigned_site_count} site${t.assigned_site_count === 1 ? '' : 's'}`}
                      size="small" sx={{
                        height: 20, fontSize: 10.5, fontWeight: 700,
                        bgcolor: '#7C3AED22', color: '#7C3AED',
                      }} />
                  )}
                  {!t.is_active && (
                    <Chip label="Inactive" size="small" sx={{
                      height: 20, fontSize: 10.5, fontWeight: 700,
                      bgcolor: 'action.hover', color: 'text.secondary',
                    }} />
                  )}
                  <CustomerScopeChip customerId={t.customer_id} />
                </Stack>
                {t.description && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {t.description}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Edit template">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditing(t) }}>
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete template">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(t) }}
                  sx={{ color: '#DC2626' }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <ChevronRightIcon sx={{ color: 'text.disabled' }} />
            </Box>
          ))}
        </Stack>
      )}

      {(createOpen || editing) && (
        <TemplateFormDialog template={editing}
          onClose={() => { setCreateOpen(false); setEditing(null) }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['issue-templates'] })
            setCreateOpen(false); setEditing(null)
          }} />
      )}
    </Box>
  )
}

// ─── Level 2: template detail (items CRUD) ───────────────────────

function TemplateDetail({ template, onBack, onTemplateChanged }: {
  template: IssueTemplate
  onBack: () => void
  onTemplateChanged: (t: IssueTemplate) => void
}) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<IssueTemplateItem | null>(null)
  const [editTemplate, setEditTemplate] = useState(false)
  const confirmDlg = useConfirm()

  const itemsQ = useQuery({
    queryKey: ['issue-template-items', template.id],
    queryFn: () => api.issueTemplates.listItems(template.id),
  })
  const items: IssueTemplateItem[] = Array.isArray(itemsQ.data) ? itemsQ.data : []

  const deleteM = useMutation({
    mutationFn: (itemId: string) => api.issueTemplates.removeItem(template.id, itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issue-template-items', template.id] })
      qc.invalidateQueries({ queryKey: ['issue-templates'] })
      toast.success('Item deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const toggleM = useMutation({
    mutationFn: (it: IssueTemplateItem) =>
      api.issueTemplates.updateItem(template.id, it.id, { is_active: !it.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['issue-template-items', template.id] })
      qc.invalidateQueries({ queryKey: ['issue-templates'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const handleDelete = async (it: IssueTemplateItem) => {
    const ok = await confirmDlg({
      title: 'Delete this item?',
      message: `"${it.label}" will be removed from the reporter's dropdown. Historical requests filed against this item stay intact.`,
      confirmLabel: 'Delete item',
      variant: 'danger',
    })
    if (ok) deleteM.mutate(it.id)
  }

  return (
    <Box>
      {/* Breadcrumb / back */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { mx: 1 } }}>
          <Link component="button" onClick={onBack}
            sx={{ fontSize: 13, color: 'text.secondary', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Issue templates
          </Link>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{template.name}</Typography>
        </Breadcrumbs>
      </Stack>

      {/* Template header */}
      <Box sx={{
        p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
        mb: 2.5,
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{template.name}</Typography>
              {!template.is_active && (
                <Chip label="Inactive" size="small" sx={{
                  height: 20, fontSize: 10.5, fontWeight: 700,
                  bgcolor: 'action.hover', color: 'text.secondary',
                }} />
              )}
            </Stack>
            {template.description && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {template.description}
              </Typography>
            )}
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.25 }}>
              <Chip label={`${template.active_item_count} active / ${template.item_count} total`} size="small"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#3B82F622', color: '#3B82F6' }} />
              <Chip label={`Assigned to ${template.assigned_site_count} site${template.assigned_site_count === 1 ? '' : 's'}`} size="small"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#7C3AED22', color: '#7C3AED' }} />
              <CustomerScopeChip customerId={template.customer_id} />
            </Stack>
          </Box>
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
            onClick={() => setEditTemplate(true)}
            sx={{ borderRadius: 2, fontWeight: 600 }}>
            Edit
          </Button>
        </Stack>
      </Box>

      {/* Items list */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Items ({items.length})</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          Add item
        </Button>
      </Stack>

      {itemsQ.isLoading ? (
        <Typography sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>Loading…</Typography>
      ) : items.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 6, border: '1px dashed', borderColor: 'divider',
          borderRadius: 2, color: 'text.disabled',
        }}>
          <QuestionAnswerOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No items in this template yet</Typography>
          <Typography variant="body2" sx={{ maxWidth: 400, mx: 'auto' }}>
            Add items to give reporters a preset list. Until an item is added,
            sites assigned to this template still fall back to a free-text input.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {items.map((it) => {
            const priorityMeta = it.priority ? PRIORITY_META[it.priority] : null
            return (
              <Box key={it.id} sx={{
                p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 2,
                opacity: it.is_active ? 1 : 0.55,
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {it.label}
                    </Typography>
                    {priorityMeta && (
                      <Chip label={priorityMeta.label} size="small" sx={{
                        height: 20, fontSize: 10.5, fontWeight: 700,
                        bgcolor: `${priorityMeta.color}22`, color: priorityMeta.color,
                      }} />
                    )}
                    {!it.is_active && (
                      <Chip label="Inactive" size="small" sx={{
                        height: 20, fontSize: 10.5, fontWeight: 700,
                        bgcolor: 'action.hover', color: 'text.secondary',
                      }} />
                    )}
                  </Stack>
                  {it.description && (
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {it.description}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={it.is_active ? 'Hide from public form' : 'Show on public form'}>
                  <Switch size="small" checked={it.is_active} onChange={() => toggleM.mutate(it)} />
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setEditing(it)}>
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(it)}
                    sx={{ color: '#DC2626' }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          })}
        </Stack>
      )}

      {(createOpen || editing) && (
        <ItemFormDialog templateId={template.id} item={editing}
          onClose={() => { setCreateOpen(false); setEditing(null) }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['issue-template-items', template.id] })
            qc.invalidateQueries({ queryKey: ['issue-templates'] })
            setCreateOpen(false); setEditing(null)
          }} />
      )}

      {editTemplate && (
        <TemplateFormDialog template={template}
          onClose={() => setEditTemplate(false)}
          onSaved={async () => {
            setEditTemplate(false)
            const fresh = await api.issueTemplates.get(template.id)
            onTemplateChanged(fresh)
            qc.invalidateQueries({ queryKey: ['issue-templates'] })
          }} />
      )}
    </Box>
  )
}

// ─── Create / edit template ──────────────────────────────────────

function TemplateFormDialog({ template, onClose, onSaved }: {
  template: IssueTemplate | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!template
  const [name, setName] = useState(template?.name ?? '')
  const [description, setDescription] = useState(template?.description ?? '')
  const [isActive, setIsActive] = useState(template?.is_active ?? true)
  const [customerId, setCustomerId] = useState<string | null>(template?.customer_id ?? null)

  const createM = useMutation({
    mutationFn: () => api.issueTemplates.create({
      name: name.trim(),
      description: description.trim() || undefined,
      is_active: isActive,
      customer_id: customerId,
    }),
    onSuccess: () => { toast.success('Template created'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const updateM = useMutation({
    mutationFn: () => api.issueTemplates.update(template!.id, {
      name: name.trim(),
      description: description.trim() || null,
      is_active: isActive,
      customer_id: customerId,
    }),
    onSuccess: () => { toast.success('Template saved'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const saving = createM.isPending || updateM.isPending
  const dirty = name.trim().length > 0

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
        {isEdit ? 'Edit template' : 'New template'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Template name" required autoFocus
            value={name} onChange={(e) => setName(e.target.value)}
            size="small" fullWidth
            placeholder="e.g. Warehouse issues, Office issues, Kitchen issues"
            helperText="A short internal name. Reporters never see this."
          />
          <TextField label="Description" multiline minRows={3}
            value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth
            placeholder="Optional. Reminds admins what this template covers."
          />

          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="All = assignable to any customer's sites. Pick one to scope this template."
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Active</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Sites assigned to an inactive template fall back to a free-text input.
              </Typography>
            </Box>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!dirty || saving}
          onClick={() => (isEdit ? updateM.mutate() : createM.mutate())}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Create / edit item ──────────────────────────────────────────

function ItemFormDialog({ templateId, item, onClose, onSaved }: {
  templateId: string
  item: IssueTemplateItem | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!item
  const [label, setLabel] = useState(item?.label ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [priority, setPriority] = useState<'' | 'low' | 'medium' | 'high' | 'critical'>(
    item?.priority ?? '',
  )
  const [isActive, setIsActive] = useState(item?.is_active ?? true)

  const createM = useMutation({
    mutationFn: () => api.issueTemplates.createItem(templateId, {
      label: label.trim(),
      description: description.trim() || undefined,
      priority: priority === '' ? undefined : priority,
      is_active: isActive,
    }),
    onSuccess: () => { toast.success('Item added'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const updateM = useMutation({
    mutationFn: () => api.issueTemplates.updateItem(templateId, item!.id, {
      label: label.trim(),
      description: description.trim() || null,
      priority: priority === '' ? null : priority,
      is_active: isActive,
    }),
    onSuccess: () => { toast.success('Item saved'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const saving = createM.isPending || updateM.isPending
  const dirty = label.trim().length > 0

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
        {isEdit ? 'Edit item' : 'New item'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Label" required autoFocus
            value={label} onChange={(e) => setLabel(e.target.value)}
            size="small" fullWidth
            placeholder="e.g. AC not cooling, Broken chair, Water leak"
            helperText="Shown in the reporter's dropdown."
          />
          <TextField label="Description" multiline minRows={2}
            value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth
            placeholder="Optional. Shown to the reporter when they pick this item."
          />

          <Divider />

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
              Priority preset (optional)
            </Typography>
            <Stack direction="row" spacing={1}>
              {[
                { key: ''         as const, label: 'No preset', color: '#94A3B8' },
                { key: 'low'      as const, label: 'Low',       color: PRIORITY_META.low!.color },
                { key: 'medium'   as const, label: 'Medium',    color: PRIORITY_META.medium!.color },
                { key: 'high'     as const, label: 'High',      color: PRIORITY_META.high!.color },
                { key: 'critical' as const, label: 'Critical',  color: PRIORITY_META.critical!.color },
              ].map((p) => (
                <Box key={p.key || 'none'}
                  onClick={() => setPriority(p.key)}
                  sx={{
                    flex: 1, py: 1.25, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: priority === p.key ? p.color : 'divider',
                    bgcolor: priority === p.key ? `${p.color}18` : 'transparent',
                    transition: 'all 120ms', '&:hover': { borderColor: p.color },
                  }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: p.color, mx: 'auto', mb: 0.5 }} />
                  <Typography sx={{ fontSize: 11.5, fontWeight: priority === p.key ? 700 : 500,
                    color: priority === p.key ? p.color : 'text.secondary' }}>
                    {p.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
              When set, the reporter's form auto-picks this priority when they select this item.
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Show on public form</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Turn off to hide this item without deleting it.
              </Typography>
            </Box>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!dirty || saving}
          onClick={() => (isEdit ? updateM.mutate() : createM.mutate())}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add item'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
